import React, { useState } from 'react';

// Helper function to ensure date is valid
const ensureValidDate = (date, fallback = new Date()) => {
  if (!date) return fallback;
  const dateObj = date instanceof Date ? date : new Date(date);
  return isNaN(dateObj.getTime()) ? fallback : dateObj;
};

const DatePicker = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  initialMode = 'range',
  initialStartDate = new Date(),
  initialEndDate = new Date(Date.now() + 6 * 24 * 60 * 60 * 1000),
  initialSingleDate = new Date(),
  title = "Select Date",
  hideModeToggle = false
}) => {
  // Ensure all initial dates are valid
  const validInitialStartDate = ensureValidDate(initialStartDate, new Date());
  const validInitialEndDate = ensureValidDate(initialEndDate, new Date(Date.now() + 6 * 24 * 60 * 60 * 1000));
  const validInitialSingleDate = ensureValidDate(initialSingleDate, new Date());
  
  const [datePickerMode, setDatePickerMode] = useState(initialMode);
  const [startDate, setStartDate] = useState(validInitialStartDate);
  const [endDate, setEndDate] = useState(validInitialEndDate);
  const [singleDate, setSingleDate] = useState(validInitialSingleDate);

  // Format date for display
  const formatDate = (date) => {
    const validDate = ensureValidDate(date, new Date());
    return validDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Handle date changes
  const handleStartDateChange = (e) => {
    const newStartDate = ensureValidDate(new Date(e.target.value), new Date());
    setStartDate(newStartDate);
    // Ensure end date is not before start date
    const validEndDate = ensureValidDate(endDate, newStartDate);
    if (validEndDate < newStartDate) {
      setEndDate(newStartDate);
    }
  };

  const handleEndDateChange = (e) => {
    const newEndDate = ensureValidDate(new Date(e.target.value), new Date());
    setEndDate(newEndDate);
  };

  const handleSingleDateChange = (e) => {
    const newSingleDate = ensureValidDate(new Date(e.target.value), new Date());
    setSingleDate(newSingleDate);
  };

  // Confirm date selection
  const handleConfirm = () => {
    const result = {
      mode: datePickerMode,
      singleDate: datePickerMode === 'single' ? ensureValidDate(singleDate, new Date()) : null,
      startDate: datePickerMode === 'range' ? ensureValidDate(startDate, new Date()) : null,
      endDate: datePickerMode === 'range' ? ensureValidDate(endDate, new Date()) : null
    };
    
    onConfirm(result);
    onClose();
  };

  // Reset to initial values when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setDatePickerMode(initialMode);
      setStartDate(ensureValidDate(initialStartDate, new Date()));
      setEndDate(ensureValidDate(initialEndDate, new Date(Date.now() + 6 * 24 * 60 * 60 * 1000)));
      setSingleDate(ensureValidDate(initialSingleDate, new Date()));
    }
  }, [isOpen, initialMode, initialStartDate, initialEndDate, initialSingleDate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        {title && (
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        {!title && (
          <div className="flex justify-end mb-4">
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Mode Toggle */}
        {!hideModeToggle && (
          <div className="mb-6">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setDatePickerMode('single')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  datePickerMode === 'single'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Single Date
              </button>
              <button
                onClick={() => setDatePickerMode('range')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  datePickerMode === 'range'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'bg-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                Date Range
              </button>
            </div>
          </div>
        )}
        
        {/* Single Date Selection */}
        {datePickerMode === 'single' && (
          <div className="space-y-4">
            <div>
              {!hideModeToggle && (
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="singleDate">
                  Select Date(MM/DD/YYYY):
                </label>
              )}
              <input
                type="date"
                id="singleDate"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500 cursor-pointer"
                value={ensureValidDate(singleDate, new Date()).toISOString().split('T')[0]}
                onChange={handleSingleDateChange}
                onClick={(e) => {
                  // Open native date picker on click (user gesture required)
                  if (e.target.showPicker) {
                    e.target.showPicker();
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent typing in the date input, but allow opening picker with Enter or Space
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.target.showPicker?.();
                  } else if (e.key !== 'Tab' && e.key !== 'Escape') {
                    // Prevent all other key inputs except Tab and Escape
                    e.preventDefault();
                  }
                }}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        )}

        {/* Date Range Selection */}
        {datePickerMode === 'range' && (
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="startDate">
                Start Date(MM/DD/YYYY):
              </label>
              <input
                type="date"
                id="startDate"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500 cursor-pointer"
                value={ensureValidDate(startDate, new Date()).toISOString().split('T')[0]}
                onChange={handleStartDateChange}
                onClick={(e) => {
                  // Open native date picker on click
                  if (e.target.showPicker) {
                    e.target.showPicker();
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent typing in the date input, but allow opening picker with Enter or Space
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.target.showPicker?.();
                  } else if (e.key !== 'Tab' && e.key !== 'Escape') {
                    // Prevent all other key inputs except Tab and Escape
                    e.preventDefault();
                  }
                }}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="endDate">
                End Date(MM/DD/YYYY):
              </label>
              <input
                type="date"
                id="endDate"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500 cursor-pointer"
                value={ensureValidDate(endDate, new Date()).toISOString().split('T')[0]}
                onChange={handleEndDateChange}
                onClick={(e) => {
                  // Open native date picker on click
                  if (e.target.showPicker) {
                    e.target.showPicker();
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent typing in the date input, but allow opening picker with Enter or Space
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.target.showPicker?.();
                  } else if (e.key !== 'Tab' && e.key !== 'Escape') {
                    // Prevent all other key inputs except Tab and Escape
                    e.preventDefault();
                  }
                }}
                min={ensureValidDate(startDate, new Date()).toISOString().split('T')[0]}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6 gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 bg-blue-600 !text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
            style={{ color: 'white' }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
